import { useState } from 'react'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Users, MapPin, TrendingUp, Award, ArrowRight, CheckCircle2, Send } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Select } from '@/components/ui/select'
import { Card } from '@/components/ui/card'

const franchiseTypes = [
  {
    title: 'Distribution Franchise',
    investment: '₹25-50 Lakhs',
    description: 'Become a regional distributor for GSM products and services.',
    benefits: ['Exclusive territory', 'Product training', 'Marketing support', 'Margin protection'],
  },
  {
    title: 'Service Partner',
    investment: '₹10-25 Lakhs',
    description: 'Provide installation and maintenance services in your region.',
    benefits: ['Service training', 'Certification program', 'Priority leads', 'Recurring revenue'],
  },
  {
    title: 'Business Associate',
    investment: '₹5-10 Lakhs',
    description: 'Partner with us for tender participation and project referrals.',
    benefits: ['Tender alerts', 'Commission structure', 'Documentation support', 'Relationship manager'],
  },
]

const partnershipBenefits = [
  { icon: MapPin, title: 'Territory Protection', description: 'Exclusive operating territory' },
  { icon: TrendingUp, title: 'Growth Potential', description: 'Scalable business model' },
  { icon: Users, title: 'Training & Support', description: 'Comprehensive training programs' },
  { icon: Award, title: 'Brand Recognition', description: 'Established market presence' },
]

const requirements = [
  'Minimum investment capacity as per franchise type',
  'Dedicated office space in prime location',
  'Team of 3-5 persons for operations',
  'Experience in B2B sales preferred',
  'Strong local network and connections',
  'Commitment to quality and customer satisfaction',
]

export default function Franchise() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    experience: '',
    investment: '',
    message: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)
    await new Promise(resolve => setTimeout(resolve, 2000))
    setIsSubmitting(false)
    setIsSuccess(true)
    setTimeout(() => setIsSuccess(false), 3000)
  }

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32">
        <div className="absolute inset-0 bg-gradient-to-b from-accent-900/30 to-transparent" />
        <div className="container-premium relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 mb-6">
              <Users className="w-4 h-4 text-accent-400" />
              <span className="text-sm text-graphite-300">Partnership Opportunities</span>
            </div>
            <h1 className="heading-xl text-white mb-6">
              Become a <span className="gradient-text-gold">Partner</span>
            </h1>
            <p className="text-xl text-graphite-300">
              Join our growing network of partners and distributors across India.
              Build a successful business with GSM Corporation.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Partnership Benefits */}
      <section className="py-12 border-y border-white/5">
        <div className="container-premium">
          <div className="grid md:grid-cols-4 gap-6">
            {partnershipBenefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card variant="glass" className="p-6 text-center">
                  <benefit.icon className="w-8 h-8 mx-auto mb-3 text-accent-400" />
                  <h3 className="text-white font-semibold mb-1">{benefit.title}</h3>
                  <p className="text-sm text-graphite-400">{benefit.description}</p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Franchise Types */}
      <section className="section-padding">
        <div className="container-premium">
          <div className="text-center mb-12">
            <h2 className="heading-lg text-white mb-4">
              Partnership <span className="gradient-text">Options</span>
            </h2>
            <p className="text-graphite-400 max-w-2xl mx-auto">
              Choose from multiple partnership models based on your investment capacity and business goals.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {franchiseTypes.map((type, index) => (
              <motion.div
                key={type.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card variant="premium" className="h-full p-8">
                  <h3 className="text-xl font-bold text-white mb-2">{type.title}</h3>
                  <p className="text-accent-400 font-semibold mb-4">{type.investment}</p>
                  <p className="text-graphite-400 mb-6">{type.description}</p>

                  <div className="space-y-3">
                    {type.benefits.map((benefit, i) => (
                      <div key={i} className="flex items-center gap-2 text-graphite-300">
                        <CheckCircle2 className="w-4 h-4 text-primary-400" />
                        <span>{benefit}</span>
                      </div>
                    ))}
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Requirements */}
      <section className="section-padding bg-gradient-to-b from-graphite-950 via-primary-950/10 to-graphite-950">
        <div className="container-premium">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="heading-lg text-white mb-6">
                Requirements to <span className="gradient-text-cyan">Partner</span>
              </h2>
              <p className="text-graphite-300 mb-8">
                We look for committed partners who share our vision of delivering quality
                solutions to institutions across India.
              </p>

              <div className="space-y-4">
                {requirements.map((req, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center gap-3"
                  >
                    <CheckCircle2 className="w-5 h-5 text-accent-400 flex-shrink-0" />
                    <span className="text-graphite-300">{req}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Application Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card variant="premium" className="p-8">
                <h3 className="text-xl font-bold text-white mb-6">Apply Now</h3>

                {isSuccess ? (
                  <div className="text-center py-8">
                    <CheckCircle2 className="w-16 h-16 mx-auto text-green-400 mb-4" />
                    <h4 className="text-lg font-semibold text-white mb-2">Application Submitted!</h4>
                    <p className="text-graphite-400">We'll contact you within 48 hours.</p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid md:grid-cols-2 gap-4">
                      <Input
                        label="Full Name *"
                        placeholder="Your name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                      />
                      <Input
                        label="Phone *"
                        placeholder="+91 XXXXX XXXXX"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        required
                      />
                    </div>

                    <Input
                      label="Email *"
                      type="email"
                      placeholder="your@email.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                    />

                    <div className="grid md:grid-cols-2 gap-4">
                      <Input
                        label="City *"
                        placeholder="Your city"
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        required
                      />
                      <Select
                        label="Investment Capacity"
                        options={[
                          { value: '', label: 'Select Range' },
                          { value: '5-10', label: '₹5-10 Lakhs' },
                          { value: '10-25', label: '₹10-25 Lakhs' },
                          { value: '25-50', label: '₹25-50 Lakhs' },
                          { value: '50+', label: '₹50+ Lakhs' },
                        ]}
                        value={formData.investment}
                        onChange={(e) => setFormData({ ...formData, investment: e.target.value })}
                      />
                    </div>

                    <Textarea
                      label="Your Experience (Optional)"
                      placeholder="Tell us about your business experience..."
                      rows={3}
                      value={formData.experience}
                      onChange={(e) => setFormData({ ...formData, experience: e.target.value })}
                    />

                    <Button type="submit" variant="gold" className="w-full" loading={isSubmitting}>
                      {isSubmitting ? 'Submitting...' : 'Submit Application'}
                      {!isSubmitting && <Send className="ml-2 w-4 h-4" />}
                    </Button>
                  </form>
                )}
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding">
        <div className="container-premium">
          <Card variant="premium" className="p-12 text-center">
            <h2 className="heading-md text-white mb-4">Have Questions?</h2>
            <p className="text-graphite-400 mb-8 max-w-2xl mx-auto">
              Contact our partnership team to learn more about opportunities in your region.
            </p>
            <Button variant="primary" size="lg" asChild>
              <Link to="/contact">
                Contact Us
                <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </Button>
          </Card>
        </div>
      </section>
    </div>
  )
}